import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { KeycloakAuthorizationService } from 'src/app/auth/keycloak.authorization.service';
import { timer, tap } from 'rxjs';
import { SigningTaskStatus, SigningFlowStatus, QTableTaskIcon, ButtonType } from 'src/app/core/constants/app.enums';
import { SignerDetail } from 'src/app/core/models/interfaces/SignerDetail';
import { SigningFlowTableData } from 'src/app/core/models/interfaces/SigningFlowTableData';
import { SigningFlowManager } from 'src/app/core/models/data/SingingFlowManager';

@Component({
    selector: 'app-qtable',
    templateUrl: './qtable.component.html',
    styleUrl: './qtable.component.css',
    animations: [
        // Animacia vysunutia detailnej tabulky o podpisovateloch 
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
        // Animacia rotovania sipky pri vysunuti detailnej tabulky
        trigger('rotateArrow', [
            state('collapsed', style({ transform: 'rotate(0deg)' })),
            state('expanded', style({ transform: 'rotate(180deg)' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class QTableComponent implements OnInit{
  @Input() columnsToDisplay: string[] = [];
  @Input() innerColumnsToDisplay: string[] = [];
  @ViewChild(MatTable) table!: MatTable<any>;
  @Output() downloadFile = new EventEmitter<string>();
  
  dataSource: SigningFlowTableData[] = []; 
  expandedRow: any | null;
  SigningTaskStatus = SigningTaskStatus;
  SigningFlowStatus = SigningFlowStatus;
  ButtonType = ButtonType;

  userEmail = "";

  totalItems = 0;
  pageIndex = 0;
  pageSize = 10;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private keycloakService: KeycloakAuthorizationService,
    private signingFlowManager: SigningFlowManager
  ){}

  ngOnInit(): void {
    this.keycloakService.loadUserProfile().then(profile => {
      this.userEmail = profile?.email!
    });
    
    this.signingFlowManager.setRefreshTableCallback(this.refreshTableCallback);
    this.signingFlowManager.setRefreshPaginatorAfterFilterCallback(this.refreshPaginatorAfterFilterCallback);
    this.refreshTableCallback();
  }
  
  getInnerDataSource(innerItems: SignerDetail[]): MatTableDataSource<SignerDetail> {
    return new MatTableDataSource(innerItems);
  }

  expandRow(row: any): void {
    this.expandedRow = this.expandedRow === row ? null : row;
  }

  onArrowClick(row: any, event: MouseEvent): void {
    event.stopPropagation();
    this.expandRow(row);
  }

  getIconBasedOnSigningTaskStatus(status: SigningTaskStatus): string{
    if(status === SigningTaskStatus.PENDING){
      return QTableTaskIcon.HOURGLASS_EMPTY;
    }
    else if(status === SigningTaskStatus.COMPLETED){
      return QTableTaskIcon.CHECK;
    }
    else if(status === SigningTaskStatus.DECLINED){
      return QTableTaskIcon.CLOSE;
    }
    else if(status === SigningTaskStatus.FAILED){
      return QTableTaskIcon.CLOSE;
    }
    else return "";
  }

  getLabelBasedOnSigningTaskStatus(status: SigningTaskStatus):string {
    if(status === SigningTaskStatus.PENDING){
      return this.translate.instant('GLOBAL_STATE_PENDING');
    }
    else if(status === SigningTaskStatus.COMPLETED){
      return this.translate.instant('GLOBAL_STATE_COMPLETED');
    }
    else if(status === SigningTaskStatus.DECLINED){
      return this.translate.instant('GLOBAL_STATE_DECLINED');
    }
    else if(status === SigningTaskStatus.FAILED){
      return this.translate.instant('GLOBAL_STATE_FAILED');
    }
    else return "";
  }

  getIconBasedOnSigningFlowStatus(status: SigningFlowStatus): string{
    if(status === SigningFlowStatus.PENDING){
      return QTableTaskIcon.HOURGLASS_EMPTY;
    }
    else if(status === SigningFlowStatus.IN_PROGRESS){
      return QTableTaskIcon.HOURGLASS_EMPTY;
    }
    else if(status === SigningFlowStatus.IN_PROGRESS_WITH_ERRORS){
      return QTableTaskIcon.HOURGLASS_EMPTY;
    }
    else if(status === SigningFlowStatus.COMPLETED){
      return QTableTaskIcon.CHECK;
    }
    else if(status === SigningFlowStatus.EXPIRED){
      return QTableTaskIcon.CLOSE;
    }
    else if(status === SigningFlowStatus.CANCELLED){
      return QTableTaskIcon.CLOSE;
    }
    else return "";
  }

  getLabelBasedOnSigningFlowStatus(status: SigningFlowStatus):string {
    if(status === SigningFlowStatus.PENDING){
      return this.translate.instant('GLOBAL_STATE_PENDING');
    }
    else if(status === SigningFlowStatus.COMPLETED){
      return this.translate.instant('GLOBAL_STATE_COMPLETED');
    }
    else if(status === SigningFlowStatus.CANCELLED){
      return this.translate.instant('GLOBAL_STATE_CANCELLED');
    }
    else if(status === SigningFlowStatus.IN_PROGRESS){
      return this.translate.instant('GLOBAL_STATE_IN_PROGRESS');
    }
    else if(status === SigningFlowStatus.IN_PROGRESS_WITH_ERRORS){
      return this.translate.instant('GLOBAL_STATE_IN_PROGRESS_WITH_ERRORS');
    }
    else if(status === SigningFlowStatus.EXPIRED){
      return this.translate.instant('GLOBAL_STATE_EXPIRED');
    }
    else return "";
  }

  onCancelSigningFlow = this._onCancelSigningFlow.bind(this)
  _onCancelSigningFlow(referenceCode: string, event: Event){
    event.stopPropagation();
    this.signingFlowManager.cancelSigningFlow(referenceCode);
  }

  onDownloadFile = this._onDownloadFile.bind(this)
  _onDownloadFile(referenceCode: string, event: Event){
    event.stopPropagation();
    this.signingFlowManager.downloadFile(referenceCode);
  }

  onResendRequest = this._onResendRequest.bind(this)
  _onResendRequest(referenceCode: string, event: Event){
    event.stopPropagation();
    this.signingFlowManager.resendEmail(referenceCode);
  }

  getNumberOfSignedSlashTotal(row: SigningFlowTableData): string{
    var numOfAlreadySigned = 0;
    var totalSigns = row.TABLE_SIGNER_DETAILS.length;

    row.TABLE_SIGNER_DETAILS.forEach((innerTableRow) => {
      if(innerTableRow.TABLE_TASK_STATUS === SigningTaskStatus.COMPLETED){
        numOfAlreadySigned += 1;
      }
    })

    return numOfAlreadySigned + "/" + totalSigns;
  }

  isFlowInProgress(row: SigningFlowTableData): boolean {
    return row.TABLE_FLOW_STATUS === SigningFlowStatus.IN_PROGRESS || row.TABLE_FLOW_STATUS === SigningFlowStatus.PENDING || row.TABLE_FLOW_STATUS === SigningFlowStatus.IN_PROGRESS_WITH_ERRORS
  }

  displayCancelButton(row: SigningFlowTableData): boolean{
    return row.TABLE_FLOW_STATUS !== SigningFlowStatus.CANCELLED && row.TABLE_FLOW_STATUS !== SigningFlowStatus.COMPLETED && row.TABLE_FLOW_STATUS !== SigningFlowStatus.EXPIRED;
  }
  
  displayResendUndescoreText(row: SigningFlowTableData, innerRow: SignerDetail): boolean{
    if(row.TABLE_FLOW_ORDERED){
      var alreadySignedCounter = 0;
      row.TABLE_SIGNER_DETAILS.forEach((_innerRow) => {
        if(_innerRow.TABLE_TASK_STATUS === SigningTaskStatus.COMPLETED){
          alreadySignedCounter++;
        }
      });
      return innerRow.TABLE_TASK_INDEX === alreadySignedCounter &&
        innerRow.TABLE_TASK_STATUS !== SigningTaskStatus.COMPLETED &&
        row.TABLE_FLOW_STATUS !== SigningFlowStatus.CANCELLED && 
        row.TABLE_FLOW_STATUS !== SigningFlowStatus.COMPLETED && 
        row.TABLE_FLOW_STATUS !== SigningFlowStatus.EXPIRED;
    }
    return innerRow.TABLE_TASK_STATUS !== SigningTaskStatus.COMPLETED &&
      row.TABLE_FLOW_STATUS !== SigningFlowStatus.CANCELLED && 
      row.TABLE_FLOW_STATUS !== SigningFlowStatus.COMPLETED && 
      row.TABLE_FLOW_STATUS !== SigningFlowStatus.EXPIRED;
  }

  formatDateTime(createdAt: string, pattern: string): string{
    const localDate = new Date(createdAt); // automatically converts to local time
    return formatDate(localDate, pattern, 'sk');
  }

  displaySignByMeOption(innerRow: SignerDetail){
    return this.userEmail === innerRow.TABLE_TASK_EMAIL && 
      innerRow.TABLE_TASK_STATUS !== SigningTaskStatus.DECLINED && 
      innerRow.TABLE_TASK_STATUS !== SigningTaskStatus.FAILED
  }

  onRouteToSigningTask(referenceCode: string){
    var languagePreference = window.localStorage.getItem('languagePreference');
    if(languagePreference !== null){
      this.router.navigate([`/signing-task/${referenceCode}`],{
        queryParams: { language: languagePreference.toUpperCase() }
      });
    }
    else{
      this.router.navigate(['/signing-task/'+referenceCode]);
    }
  }

  public refreshTableCallback = this._refreshTableCallback.bind(this);
  _refreshTableCallback(){
      this.dataSource = this.signingFlowManager.getSigningFlowTableData()
      // automatically unroll first record => removed feature
      // if(this.dataSource.length > 0){ 
      //     timer(300).pipe(tap(() => {
      //         if(this.expandedRow !== this.dataSource[0]){
      //             this.expandRow(this.dataSource[0])
      //         }
      //     })).subscribe();
      // }
      this.totalItems = this.signingFlowManager.getTotalCount();
      setTimeout(() => this.table.renderRows());
  }

   public refreshPaginatorAfterFilterCallback = this._refreshPaginatorAfterFilterCallback.bind(this);
  _refreshPaginatorAfterFilterCallback(){
      this.pageIndex = this.signingFlowManager.getCurrentPage();
      setTimeout(() => this.table.renderRows());
  }

  // Once clicked on next page, we dynamically fetch next page of data
  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex
    this.pageSize = event.pageSize

    this.signingFlowManager.onPageEvent(event);
  }
}